import React, { useState, useEffect } from 'react';
import {
  ThemeProvider,
  createTheme,
  CssBaseline,
  Alert,
  Button,
  Box,
  Snackbar
} from '@mui/material';
import { RouterProvider } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SettingsProvider, useSettings } from './contexts/SettingsContext';
import { RoleProvider } from './contexts/RoleContext';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import { ToastProvider } from './contexts/ToastContext';
import { createAppRouter } from './routes';
import { fetchOddsWithPeriodMarkets } from './services/oddsApi';
import { sendEmailVerification } from 'firebase/auth';

// Site-wide button rule (Yianni, 2026-11-04): every clickable
// button gets a filled background — no bare/text-only buttons.
// Dark mode fills with the orange accent (#f97316 family); light
// mode fills with the primary blue (#26275b family). Button text
// is WHITE in both modes (Yianni: "the text should all be one
// color" — text on a filled control is white), and so is every
// icon or image sitting on a filled button. Applied once, here
// at the theme level, so every screen inherits it; a
// component-local sx can still paint a specific filled state (e.g. a selected leg's green, the slip
// card's chips). Selector chips keep a lighter fill for their
// unselected state so selected (filled, color-prop) chips stay
// distinguishable — both states are filled.
const buttonColorsFor = (mode) => (mode === 'dark'
  ? {
      fill: '#f97316',
      fillHover: '#ea580c',
      fillSelected: '#c2410c',
      text: '#ffffff',
      textDisabled: 'rgba(255, 255, 255, 0.75)',
      fillDisabled: 'rgba(249, 115, 22, 0.35)',
      chipFill: '#7c3a06',
      chipFillHover: '#8f4508',
      chipText: '#ffffff'
    }
  : {
      fill: '#26275b',
      fillHover: '#1b1c42',
      fillSelected: '#171838',
      text: '#ffffff',
      textDisabled: '#ffffff',
      fillDisabled: 'rgba(38, 39, 91, 0.35)',
      chipFill: '#55568f',
      chipFillHover: '#464a7d',
      chipText: '#ffffff'
    });

// Chat surface tokens (Game Chats screen + GameChatDialog). The
// chat needs its own layer stack so the message area, the bubbles
// and the input read as separate surfaces in both modes. Own
// bubbles take a saturated identity fill with WHITE text (the same
// rule as filled controls: text on a filled surface is white);
// other bubbles take the paper surface over a recessed area
// background, so no bubble ever matches the surface behind it.
// Before this, own bubbles used 'primary.light' with inherited
// black text in light mode (black on mid-blue) while other
// bubbles used 'background.paper' on a paper dialog (invisible),
// and in dark mode the outlined primary chips and the primary
// header icon sat dark-green on dark-green.
const chatColorsFor = (mode) => (mode === 'dark'
  ? {
      area: '#12241d',
      bubbleOwn: '#487163',
      bubbleOwnText: '#ffffff',
      bubbleOwnMeta: 'rgba(255, 255, 255, 0.75)',
      bubbleOther: '#1a2f28',
      bubbleOtherText: '#ffffff',
      avatarOwn: 'rgba(255, 255, 255, 0.22)',
      avatarOther: '#487163',
      chipAccent: '#8fd0ba',
      headerIcon: '#4ade80',
      tabIndicator: '#4ade80',
      inputBg: '#0f1f1a'
    }
  : {
      area: '#eceef3',
      bubbleOwn: '#26275b',
      bubbleOwnText: '#ffffff',
      bubbleOwnMeta: 'rgba(255, 255, 255, 0.72)',
      bubbleOther: '#ffffff',
      bubbleOtherText: '#000000',
      avatarOwn: 'rgba(255, 255, 255, 0.22)',
      avatarOther: '#55568f',
      chipAccent: '#26275b',
      headerIcon: '#26275b',
      tabIndicator: '#26275b',
      inputBg: '#eceef3'
    });

// Create theme based on mode (light or dark)
const createAppTheme = (mode) => {
  const btn = buttonColorsFor(mode);
  return createTheme({
  palette: {
    mode,
    primary: {
      main: mode === 'dark' ? '#1a4d3c' : '#26275b',
    },
    secondary: {
      main: '#4ade80', // Green for Sign Up button
    },
    error: {
      main: '#dc004e',
    },
    warning: {
      main: '#f97316', // Orange accent for highlights
    },
    background: {
      default: mode === 'dark' ? '#0f1f1a' : '#f5f5f5',
      paper: mode === 'dark' ? '#1a2f28' : '#ffffff',
    },
    text: {
      primary: mode === 'dark' ? '#ffffff' : '#000000',
      secondary: mode === 'dark' ? '#b0bec5' : '#666666',
    },
    chat: chatColorsFor(mode),
  },
  typography: {
    h6: {
      fontSize: '1.1rem',
      '@media (max-width:600px)': {
        fontSize: '1rem',
      },
    },
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiContainer: {
      styleOverrides: {
        root: {
          paddingLeft: '8px',
          paddingRight: '8px',
          '@media (min-width:600px)': {
            paddingLeft: '16px',
            paddingRight: '16px',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    // Button rule (see buttonColorsFor above): root + every
    // variant/color slot, because MUI's variant classes can
    // otherwise re-transparent the background root set.
    MuiButton: {
      styleOverrides: {
        root: {
          backgroundColor: btn.fill,
          color: btn.text,
          '&:hover': { backgroundColor: btn.fillHover },
          '&.Mui-disabled': { backgroundColor: btn.fillDisabled, color: btn.textDisabled },
        },
        contained: { backgroundColor: btn.fill, color: btn.text, '&:hover': { backgroundColor: btn.fillHover } },
        containedPrimary: { backgroundColor: btn.fill, color: btn.text, '&:hover': { backgroundColor: btn.fillHover } },
        containedSecondary: { backgroundColor: btn.fill, color: btn.text, '&:hover': { backgroundColor: btn.fillHover } },
        containedSuccess: { backgroundColor: btn.fill, color: btn.text, '&:hover': { backgroundColor: btn.fillHover } },
        containedError: { backgroundColor: btn.fill, color: btn.text, '&:hover': { backgroundColor: btn.fillHover } },
        containedWarning: { backgroundColor: btn.fill, color: btn.text, '&:hover': { backgroundColor: btn.fillHover } },
        containedInfo: { backgroundColor: btn.fill, color: btn.text, '&:hover': { backgroundColor: btn.fillHover } },
        outlined: {
          backgroundColor: btn.fill,
          color: btn.text,
          borderColor: btn.fill,
          '&:hover': { backgroundColor: btn.fillHover, borderColor: btn.fillHover },
        },
        outlinedPrimary: { backgroundColor: btn.fill, color: btn.text, borderColor: btn.fill, '&:hover': { backgroundColor: btn.fillHover, borderColor: btn.fillHover } },
        outlinedSecondary: { backgroundColor: btn.fill, color: btn.text, borderColor: btn.fill, '&:hover': { backgroundColor: btn.fillHover, borderColor: btn.fillHover } },
        outlinedError: { backgroundColor: btn.fill, color: btn.text, borderColor: btn.fill, '&:hover': { backgroundColor: btn.fillHover, borderColor: btn.fillHover } },
        text: { backgroundColor: btn.fill, color: btn.text, '&:hover': { backgroundColor: btn.fillHover } },
        textPrimary: { backgroundColor: btn.fill, color: btn.text, '&:hover': { backgroundColor: btn.fillHover } },
        textSecondary: { backgroundColor: btn.fill, color: btn.text, '&:hover': { backgroundColor: btn.fillHover } },
        textError: { backgroundColor: btn.fill, color: btn.text, '&:hover': { backgroundColor: btn.fillHover } },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          backgroundColor: btn.fill,
          color: btn.text,
          '&:hover': { backgroundColor: btn.fillHover },
          '&.Mui-disabled': { backgroundColor: btn.fillDisabled, color: btn.textDisabled },
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          backgroundColor: btn.fill,
          color: btn.text,
          '&:hover': { backgroundColor: btn.fillHover },
          '&.Mui-selected': { backgroundColor: btn.fillSelected, color: btn.text },
          '&.Mui-selected:hover': { backgroundColor: btn.fillSelected },
          '&.Mui-disabled': { backgroundColor: btn.fillDisabled, color: btn.textDisabled },
        },
      },
    },
    MuiFab: {
      styleOverrides: {
        root: {
          backgroundColor: btn.fill,
          color: btn.text,
          '&:hover': { backgroundColor: btn.fillHover },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        // Clickable chips are buttons in every way that matters.
        // Filled-variant chips carry their fill from their color
        // prop, painted solid at full strength here (the
        // label keeps the palette contrastText — white on the
        // blue/orange family). The rest get the lighter selector
        // fill so selected/unselected stay distinguishable.
        root: ({ ownerState, theme }) => {
          if (!ownerState.clickable) return {};
          if (ownerState.variant === 'filled') {
            const paletteColor = ownerState.color && ownerState.color !== 'default'
              ? theme.palette[ownerState.color]
              : null;
            if (!paletteColor) return {};
            return {
              backgroundColor: paletteColor.main,
              color: paletteColor.contrastText,
              '&:hover': { backgroundColor: paletteColor.dark },
              '& .MuiChip-deleteIcon': { color: paletteColor.contrastText },
            };
          }
          return {
            backgroundColor: btn.chipFill,
            color: btn.chipText,
            '&:hover': { backgroundColor: btn.chipFillHover },
            '& .MuiChip-deleteIcon': { color: btn.chipText },
          };
        },
      },
    },
  },
  });
};

function MainApp() {
  const { currentUser } = useAuth();
  const { theme: themeMode } = useSettings();
  const [odds, setOdds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [verificationBannerDismissed, setVerificationBannerDismissed] = useState(false);
  const [verificationSuccessBanner, setVerificationSuccessBanner] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  const [resendError, setResendError] = useState('');

  // Create theme based on user preference (default to dark/green theme)
  const theme = createAppTheme(themeMode || 'dark');

  // Check if verification banner was dismissed this session
  useEffect(() => {
    const dismissed = sessionStorage.getItem('emailVerificationDismissed');
    if (dismissed === 'true') {
      setVerificationBannerDismissed(true);
    }
    
    // Check for verification success flag from URL params
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('verified') === 'true') {
      setVerificationSuccessBanner(true);
      // Clear the URL parameter
      window.history.replaceState({}, document.title, window.location.pathname);
      // Auto-dismiss after 7 seconds
      setTimeout(() => setVerificationSuccessBanner(false), 7000);
    }
  }, []);

  const handleDismissBanner = () => {
    sessionStorage.setItem('emailVerificationDismissed', 'true');
    setVerificationBannerDismissed(true);
  };

  const handleResendVerification = async () => {
    if (!currentUser) return;
    
    setResendLoading(true);
    setResendError('');
    setResendMessage('');
    
    try {
      await sendEmailVerification(currentUser, {
        url: `${window.location.origin}/auth/action`,
        handleCodeInApp: false
      });
      setResendMessage('Verification email sent! Please check your inbox.');
      setTimeout(() => setResendMessage(''), 5000);
    } catch (err) {
      console.error('Error sending verification email:', err);
      setResendError('Failed to send verification email. Please try again later.');
      setTimeout(() => setResendError(''), 5000);
    } finally {
      setResendLoading(false);
    }
  };

  // The public odds board loads for everyone, signed in or not.
  // Login is only required for account features (watchlist, tracker,
  // subscription content) — never for reading the board.
  useEffect(() => {
    loadOdds();
  }, [currentUser]);

  // Check for referral code in URL on app load
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const referralCode = urlParams.get('ref');
    
    if (referralCode) {
      // Store referral code in localStorage for use during signup
      localStorage.setItem('referralCode', referralCode);
      console.log('Referral code detected:', referralCode);
      
      // Optional: Clean up the URL while preserving the hash/path
      if (window.history.replaceState) {
        const cleanUrl = window.location.pathname + window.location.hash;
        window.history.replaceState({}, document.title, cleanUrl);
      }
    }
  }, []);

  const loadOdds = async (sport = 'upcoming') => {
    setLoading(true);
    setError(null);
    try {
      const oddsData = await fetchOddsWithPeriodMarkets(sport);
      setOdds(oddsData);
      
    } catch (err) {
      setError('Failed to load odds. Please check your API configuration.');
      console.error('Error fetching odds:', err);
    } finally {
      setLoading(false);
    }
  };

  // Create router with current data
  const router = createAppRouter(currentUser, odds, loading, error, loadOdds);

  const showVerificationBanner = currentUser && !currentUser.emailVerified && !verificationBannerDismissed;

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      
      {/* Email Verification Success Banner */}
      {verificationSuccessBanner && (
        <Box sx={{ position: 'sticky', top: 0, zIndex: 1300 }}>
          <Alert 
            severity="success"
            onClose={() => setVerificationSuccessBanner(false)}
            sx={{ borderRadius: 0 }}
          >
            Your email has been verified successfully! You now have full access to all features.
          </Alert>
        </Box>
      )}
      
      {/* Email Verification Warning Banner */}
      {showVerificationBanner && (
        <Box sx={{ position: 'sticky', top: 0, zIndex: 1300 }}>
          <Alert 
            severity="warning"
            onClose={handleDismissBanner}
            action={
              <Button 
                color="inherit" 
                size="small" 
                onClick={handleResendVerification}
                disabled={resendLoading}
              >
                {resendLoading ? 'Sending...' : 'Resend Email'}
              </Button>
            }
            sx={{ borderRadius: 0 }}
          >
            Please verify your email address. Check your inbox for a verification link.
          </Alert>
        </Box>
      )}

      {/* Success/Error Messages for Resend */}
      <Snackbar 
        open={!!resendMessage} 
        autoHideDuration={5000} 
        onClose={() => setResendMessage('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setResendMessage('')}>
          {resendMessage}
        </Alert>
      </Snackbar>

      <Snackbar 
        open={!!resendError} 
        autoHideDuration={5000} 
        onClose={() => setResendError('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setResendError('')}>
          {resendError}
        </Alert>
      </Snackbar>

      <ToastProvider>
        <RouterProvider router={router} />
      </ToastProvider>
    </ThemeProvider>
  );
}

// Main App wrapper with AuthProvider, SettingsProvider, RoleProvider, and SubscriptionProvider
function App() {
  return (
    <AuthProvider>
      <SettingsProvider>
        <RoleProvider>
          <SubscriptionProvider>
            <MainApp />
          </SubscriptionProvider>
        </RoleProvider>
      </SettingsProvider>
    </AuthProvider>
  );
}

export default App;
